<template>
  <AppLayout>
    <div class="fleet-crud-page">
      <div class="fleet-page-head">
        <div>
          <p class="kicker">Fleet / Fuel</p>
          <h2>Fuel Request Detail</h2>
        </div>
        <RouterLink class="btn-ghost" to="/fleet/fuel">Back to Fuel</RouterLink>
      </div>

      <div class="fleet-grid detail-grid">
        <section class="panel">
          <h3>Fuel Snapshot</h3>
          <div v-if="loading">Loading...</div>
          <div v-else-if="!fuel" class="empty">Fuel record not found.</div>
          <div v-else class="detail-list">
            <p><strong>Status:</strong> {{ fuel.status || '-' }}</p>
            <p><strong>Project:</strong> {{ fuel?.project?.projectName || fuel.project_id || '-' }}</p>
            <p><strong>Vehicle:</strong> {{ fuel?.vehicle?.plateNumber || fuel.vehicle_id || '-' }}</p>
            <p><strong>Requester:</strong> {{ fuel?.user?.name || '-' }}</p>
            <p><strong>Requested Litres:</strong> {{ fuel.requested_litres ?? '-' }}</p>
            <p><strong>Allocated Litres:</strong> {{ fuel.allocated_litres ?? '-' }}</p>
            <p><strong>Cost / Litre:</strong> {{ formatCurrency(fuel.costPerLitre) }}</p>
            <p><strong>Total Cost:</strong> {{ formatCurrency(fuel.totalCost) }}</p>
            <p><strong>Odometer:</strong> {{ fuel.odometerReading ?? '-' }}</p>
            <p><strong>Rejection Reason:</strong> {{ fuel.rejectionReason || '-' }}</p>
            <p><strong>Allocation Notes:</strong> {{ fuel.allocationNotes || '-' }}</p>
            <p><strong>Created At:</strong> {{ formatDateTime(fuel.createdAt) }}</p>
          </div>
        </section>

        <div class="detail-side">
          <FleetTimeline title="Fuel Workflow Timeline" :items="timelineItems" />
          <FleetAuditExport
            file-prefix="fuel-audit"
            report-title="Fuel Audit Report"
            :report-id="reportId"
            :reviewed-by="reviewedBy"
            :reviewed-date="reviewedDate"
            :summary-rows="summaryRows"
            :timeline-items="timelineItems"
          />
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import FleetTimeline from './FleetTimeline.vue';
import FleetAuditExport from './FleetAuditExport.vue';
import fleetApi from '@/api/fleet';

export default {
  name: 'FleetFuelDetail',
  components: { AppLayout, FleetTimeline, FleetAuditExport },
  data() {
    return {
      loading: false,
      fuel: null,
    };
  },
  computed: {
    recordId() {
      return this.fuel?.id || this.fuel?.fuel_entry_id || this.$route.params.id;
    },
    reportId() {
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `FLT-FUL-${String(this.recordId)}-${datePart}`;
    },
    reviewedBy() {
      try {
        const user = JSON.parse(localStorage.getItem('user_cache') || 'null');
        return user?.name || 'System Reviewer';
      } catch (_) {
        return 'System Reviewer';
      }
    },
    reviewedDate() {
      return new Date().toLocaleDateString();
    },
    summaryRows() {
      if (!this.fuel) return [];
      return [
        { label: 'Status', value: this.fuel.status || '-' },
        { label: 'Project', value: this.fuel?.project?.projectName || this.fuel.project_id || '-' },
        { label: 'Vehicle', value: this.fuel?.vehicle?.plateNumber || this.fuel.vehicle_id || '-' },
        { label: 'Requester', value: this.fuel?.user?.name || '-' },
        { label: 'Requested Litres', value: this.fuel.requested_litres ?? '-' },
        { label: 'Allocated Litres', value: this.fuel.allocated_litres ?? '-' },
        { label: 'Cost / Litre', value: this.formatCurrency(this.fuel.costPerLitre) },
        { label: 'Total Cost', value: this.formatCurrency(this.fuel.totalCost) },
      ];
    },
    timelineItems() {
      if (!this.fuel) return [];
      const events = [
        {
          label: 'Fuel request created',
          time: this.formatDateTime(this.fuel.createdAt),
          note: `Requested ${this.fuel.requested_litres ?? '-'} litres`,
        },
      ];

      if (this.fuel.status === 'APPROVED' || this.fuel.status === 'ALLOCATED') {
        events.push({
          label: 'Line manager approved request',
          time: 'Status-based event',
          note: 'Approval captured in workflow status',
        });
      }

      if (this.fuel.status === 'ALLOCATED') {
        events.push({
          label: 'Fuel allocated',
          time: 'Status-based event',
          note: `Allocated ${this.fuel.allocated_litres ?? '-'} litres`,
        });
      }

      if (this.fuel.status === 'DECLINED') {
        events.push({
          label: 'Fuel request declined',
          time: 'Status-based event',
          note: this.fuel.rejectionReason || 'No reason captured',
        });
      }

      return events;
    },
  },
  methods: {
    formatDateTime(v) {
      if (!v) return 'N/A';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
    },
    formatCurrency(v) {
      if (v === null || v === undefined) return '-';
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(Number(v));
    },
    async loadDetail() {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        const list = await fleetApi.listFuels({ page: 1, limit: 200 });
        const rows = fleetApi.unwrapList(list);
        const fromList = rows.find((r) => String(r.id || r.fuel_entry_id) === String(id));
        if (fromList) {
          this.fuel = fromList;
          return;
        }

        const API_BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '');
        const token = JSON.parse(localStorage.getItem('access_token') || 'null');
        const res = await fetch(`${API_BASE_URL}/fuels/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Fuel fetch failed');
        this.fuel = await res.json();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load fuel record');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadDetail();
  },
};
</script>

<style scoped>
@import './fleet.shared.css';

.detail-grid {
  grid-template-columns: 1fr 1fr;
}

.detail-side {
  display: grid;
  gap: 12px;
}

.detail-list p {
  margin: 6px 0;
  font-size: 13px;
}

@media (max-width: 1024px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
